/**
 * Name boards: the text the core plans (`core/build/pois.ts`), painted.
 *
 * Every sign in a tile is drawn into one canvas — a shelf-packed atlas, a row
 * of boards per shelf — and the boards are quads in one geometry that sample
 * it. So a high street of fifty shop names is one texture, one mesh and one
 * draw call per tile, and a tile with no named places costs nothing.
 *
 * The boards are unlit: a sign reads as lit from within at night, which is
 * how shop and hospital signs look, and at noon it is a flat painted panel
 * the sun would barely change anyway.
 */

import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  LinearMipmapLinearFilter,
  Mesh,
  MeshBasicMaterial,
  SRGBColorSpace,
} from 'three';
import type { PlacedSign } from '../core/build/placement.js';

/** Atlas pixels per board height. Enough for a name read from across a street. */
let ROW = 96;
let ATLAS_WIDTH = 2048;

/**
 * The boards' resolution, 1 in full: a tile's atlas is painted on the main
 * thread and uploaded in one go, tens of megabytes at full size — on a phone,
 * half the resolution each way is a quarter of that, and still readable.
 */
export function setSignScale(scale: number): void {
  ROW = Math.round(96 * scale);
  ATLAS_WIDTH = Math.round(2048 * scale);
}
const FONT = '700 {px}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

function css(colour: number): string {
  return `#${colour.toString(16).padStart(6, '0')}`;
}

/** A red (or green) cross on a white tile, the size of the board's height. */
function drawCross(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, colour: number): void {
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(x, y, size, size);
  ctx.fillStyle = css(colour);
  const arm = size * 0.22;
  const m = size * 0.16;
  ctx.fillRect(x + size / 2 - arm / 2, y + m, arm, size - 2 * m);
  ctx.fillRect(x + m, y + size / 2 - arm / 2, size - 2 * m, arm);
}

/** Paint one board into the atlas at (x, y), `w` × `ROW` pixels. */
function paintBoard(ctx: CanvasRenderingContext2D, sign: PlacedSign, x: number, y: number, w: number): void {
  const h = ROW;
  ctx.fillStyle = css(sign.board);
  ctx.fillRect(x, y, w, h);
  // A thin frame so a white board still reads against a white wall.
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
  ctx.lineWidth = 3;
  ctx.strokeRect(x + 1.5, y + 1.5, w - 3, h - 3);

  let left = x + h * 0.28;
  if (sign.icon !== undefined) {
    const size = h * 0.72;
    drawCross(ctx, left - h * 0.08, y + (h - size) / 2, size, sign.icon === 'cross' ? 0xd52b1e : 0x0f9a45);
    left += size + h * 0.12;
  }
  const right = x + w - h * 0.28;
  const room = right - left;

  // Largest size that fits, down to a floor; past that, cut with an ellipsis.
  let px = Math.round(h * 0.58);
  let text = sign.text;
  ctx.font = FONT.replace('{px}', String(px));
  while (ctx.measureText(text).width > room && px > h * 0.36) {
    px -= 2;
    ctx.font = FONT.replace('{px}', String(px));
  }
  while (text.length > 1 && ctx.measureText(text).width > room) text = `${text.slice(0, -2).trimEnd()}…`;
  ctx.fillStyle = css(sign.textColour);
  ctx.textBaseline = 'middle';
  ctx.textAlign = sign.icon === undefined ? 'center' : 'left';
  ctx.fillText(text, sign.icon === undefined ? (left + right) / 2 : left, y + h / 2 + 1);
}

/** The tile's signs as one mesh, or undefined when it has none. */
export function buildSigns(signs: readonly PlacedSign[]): Mesh | undefined {
  if (signs.length === 0) return undefined;

  // Shelf-pack: boards in rows, each as wide as its aspect asks.
  const slots: Array<{ x: number; y: number; w: number }> = [];
  let cx = 0, cy = 0;
  for (const sign of signs) {
    const w = Math.min(ATLAS_WIDTH, Math.max(ROW, Math.round((ROW * sign.width) / sign.height)));
    if (cx + w > ATLAS_WIDTH) { cx = 0; cy += ROW; }
    slots.push({ x: cx, y: cy, w });
    cx += w;
  }
  const height = cy + ROW;

  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_WIDTH;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return undefined;

  const faces = signs.reduce((n, s) => n + (s.double === true ? 2 : 1), 0);
  const positions = new Float32Array(faces * 4 * 3);
  const uvs = new Float32Array(faces * 4 * 2);
  const indices = new Uint32Array(faces * 6);
  let f = 0;
  const face = (sign: PlacedSign, nx: number, nz: number, u0: number, v0: number, u1: number, v1: number) => {
    // Viewer's right, looking at the face along -n: (nz, 0, -nx).
    const rx = nz, rz = -nx;
    const hw = sign.width / 2, hh = sign.height / 2;
    // Sit a hair in front of the face's own side, so a two-faced board's
    // faces do not z-fight.
    const x = sign.x + nx * 0.02, z = sign.z + nz * 0.02;
    const corners = [
      [x - rx * hw, sign.y - hh, z - rz * hw, u0, v0],
      [x + rx * hw, sign.y - hh, z + rz * hw, u1, v0],
      [x + rx * hw, sign.y + hh, z + rz * hw, u1, v1],
      [x - rx * hw, sign.y + hh, z - rz * hw, u0, v1],
    ];
    corners.forEach((c, k) => {
      positions.set([c[0], c[1], c[2]], (f * 4 + k) * 3);
      uvs.set([c[3], c[4]], (f * 4 + k) * 2);
    });
    indices.set([f * 4, f * 4 + 1, f * 4 + 2, f * 4, f * 4 + 2, f * 4 + 3], f * 6);
    f++;
  };
  signs.forEach((sign, i) => {
    const s = slots[i];
    // Canvas rows run down; texture V runs up (flipY), so the top edge is 1 - y.
    const u0 = s.x / ATLAS_WIDTH, u1 = (s.x + s.w) / ATLAS_WIDTH;
    const v1 = 1 - s.y / height, v0 = 1 - (s.y + ROW) / height;
    face(sign, sign.nx, sign.nz, u0, v0, u1, v1);
    if (sign.double === true) face(sign, -sign.nx, -sign.nz, u0, v0, u1, v1);
  });

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
  geometry.setIndex(new BufferAttribute(indices, 1));
  geometry.computeBoundingSphere();

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.minFilter = LinearMipmapLinearFilter;
  texture.anisotropy = 4;
  const material = new MeshBasicMaterial({ map: texture, color: new Color(0.92, 0.92, 0.92) });
  const mesh = new Mesh(geometry, material);
  mesh.name = 'signs';
  mesh.matrixAutoUpdate = false;
  // Painting fifty names is tens of milliseconds on a phone: done a few at a
  // time between frames, and the signs shown once the atlas is complete.
  mesh.visible = false;
  let next = 0;
  const slice = () => {
    if (painting.get(mesh) !== true) return;
    const until = performance.now() + PAINT_SLICE_MS;
    while (next < signs.length && performance.now() < until) {
      paintBoard(ctx, signs[next], slots[next].x, slots[next].y, slots[next].w);
      next++;
    }
    if (next < signs.length) { setTimeout(slice, 0); return; }
    painting.delete(mesh);
    texture.needsUpdate = true;
    mesh.visible = true;
  };
  painting.set(mesh, true);
  setTimeout(slice, 0);
  return mesh;
}

/** Longest a slice of sign painting runs before yielding to the frame, ms. */
const PAINT_SLICE_MS = 3;
/** Atlases still being painted; a disposed one stops. */
const painting = new WeakMap<Mesh, boolean>();

/** Free a tile's signs: geometry, material and atlas are all its own. */
export function disposeSigns(mesh: Mesh | undefined): void {
  if (mesh === undefined) return;
  painting.delete(mesh);
  mesh.geometry.dispose();
  const material = mesh.material as MeshBasicMaterial;
  material.map?.dispose();
  material.dispose();
}
